/* eslint-disable no-useless-escape */
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const MILISEC_INTERVALS: { [unit: string]: number } = {
  year: 24 * 60 * 60 * 1000 * 365,
  month: (24 * 60 * 60 * 1000 * 365) / 12,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000,
};

/**
 * Pads a number with leading zeros to ensure it has at least two digits.
 */
const padNumber = (num: number) => {
  return num.toString().padStart(2, '0');
};

/**
 * Formats a date string or Date object into a localized ISO 8601 (Local) string.
 */
const formatDateToLocaleISOString = (dateString: string | Date) => {
  if (dateString) {
    const date = new Date(dateString);

    if (!isValidDate(date)) {
      return false;
    }

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    const isoString = `${year}-${padNumber(month)}-${padNumber(
      day,
    )}T${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}.000`;

    return isoString;
  }

  return false;
};

/**
 * Checks if a given date string contains the character 'T'.
 */
export const containsTInDate = (dateString: string) => {
  if (typeof dateString !== 'string' || dateString.trim() === '') {
    return false;
  }

  const isValid = dayjs(dateString).isValid();
  if (!isValid) return false;

  return dateString.includes('T');
};

/**
 * Validates if a date is valid or not
 */
export const isValidDate = (
  date: string | Date | undefined | null,
): boolean => {
  if (!date) return false;

  if (date instanceof Date) {
    return dayjs(date).isValid();
  }

  const parsedDate = containsTInDate(date) ? formatDateAsYMD(date) : date;

  if (!parsedDate) return false;

  return dayjs(parsedDate, 'YYYY-MM-DD', true).isValid();
};

/**
 * Remove Milliseconds to UTC Date
 * @example
 * removeMSToUTC('2024-07-08T00:00:00.000Z') // Returns '2024-07-08T00:00:00Z'
 */
export const removeMSToUTC = (date: string) => {
  return dayjs.utc(date).format('YYYY-MM-DDTHH:mm:ss[Z]');
};

/**
 * Formats a given date string or Date object to a UTC ISO string.
 */
export const formatDateToUTC = (
  dateString: string | Date | null | undefined,
) => {
  // Always early return
  if (!dateString) return false;

  const date = new Date(dateString);

  if (!isValidDate(date)) {
    return false;
  }

  const localISOString = formatDateToLocaleISOString(date);
  return localISOString;
};

/**
 * Format Date to a Readable date
 */
export const formatDateAsYMD = (dateData: string | Date) => {
  const parsedDate = dayjs(dateData);

  if (!parsedDate.isValid()) {
    return false;
  }

  return parsedDate.format('YYYY-MM-DD');
};

/**
 * Validates if the input is a valid date and returns it as an ISO 8601 string.
 */
export function toValidISOString(inputDate: string | Date | null | undefined) {
  if (!inputDate) {
    return null;
  }

  const date = new Date(inputDate);

  if (isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

/**
 * Generates a UTC date range object from provided start and end date strings.
 */
export const getUtcDateRange = (
  startDate?: string | null | undefined,
  endDate?: string | null | undefined,
) => {
  try {
    const startRange = dayjs(startDate).toDate();
    startRange.setHours(0, 0, 0, 0);

    const endRange = dayjs(endDate).toDate();
    endRange.setHours(23, 59, 59, 0);

    return {
      start_date: isValidDate(startDate)
        ? removeMSToUTC(startRange.toISOString())
        : false,
      end_date: isValidDate(endDate)
        ? removeMSToUTC(endRange.toISOString())
        : false,
    };
  } catch (error) {
    return {
      start_date: false,
      end_date: false,
    };
  }
};

/**
 * Converts a date string in the format YYYY-MM-DD to a Date object.
 */
export const formatDateStringToDateObject = (date: string): Date => {
  const [year, month, day] = date.split('-').map(Number);

  // Note that month is subtracted by 1 because months in JavaScript Date objects are 0-based (0 = January, 11 = December)
  return new Date(year, month - 1, day);
};

/**
 * Ensures a date string includes a timezone. If not, appends 'Z' to treat it as UTC.
 * @param {string} date - The date string to check for a timezone.
 * @return {string} The date string with a timezone.
 * @example
 * ensureUTC("2023-04-05T12:00"); // Returns "2023-04-05T12:00Z"
 * ensureUTC("2023-04-05T12:00+01:00"); // Returns "2023-04-05T12:00+01:00"
 */
function ensureUTC(date: string): string {
  const hasTimezone = /Z|[\+\-]\d{2}:\d{2}$/.test(date);
  return hasTimezone ? date : `${date}Z`;
}

/**
 * Formats a date string into an internationalized date string according to the given locale(s).
 * @param {string} date - The date string to format.
 * @param {string[]} locales - An array of BCP 47 language tags. Defaults to an empty array.
 * @return {string} The formatted date string.
 * @example
 * formatIntlDate("2023-04-05T12:00Z", ["en-US"]); // Returns "Apr 5, 2023"
 */
export const formatIntlDate = (
  date: string,
  locales: string[] = [],
): string => {
  const dateObj = new Date(ensureUTC(date));
  return new Intl.DateTimeFormat(locales, { dateStyle: 'medium' }).format(
    dateObj,
  );
};

/**
 * Formats a date string into a long internationalized date-time string according to the given locale(s).
 * @param {string} dateStr - The date string to format.
 * @param {string | string[]} locales - BCP 47 language tag(s). Defaults to an empty string/array.
 * @param {Intl.DateTimeFormatOptions} options - Formatting options.
 * @return {string} The formatted date-time string.
 * @example
 * formatIntlLongDateTime("2023-04-05T12:00Z", "en-US"); // Returns "April 5, 2023 at 12:00:00 PM"
 */
export const formatIntlLongDateTime = (
  dateStr: string,
  locales: string | string[] = [],
  options: Intl.DateTimeFormatOptions = {
    dateStyle: 'long',
    timeStyle: 'medium',
  },
): string => {
  const d: Date = new Date(ensureUTC(dateStr));
  return new Intl.DateTimeFormat(locales, options).format(d);
};

/**
 * Formats a date string into an internationalized date-time string according to the given locale(s).
 * @param {string} date - The date string to format.
 * @param {string[]} locales - An array of BCP 47 language tags. Defaults to an empty array.
 * @return {string} The formatted date-time string.
 * @example
 * formatIntlDatetime("2023-04-05T12:00Z", ["en-US"]); // Returns "Apr 5, 2023, 12:00 PM"
 */
export const formatIntlDatetime = (
  date: string,
  locales: string[] = [],
): string => {
  const dateObj = new Date(ensureUTC(date));
  return new Intl.DateTimeFormat(locales, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
};

/**
 * Formats a date string into a medium internationalized date-time string, possibly adjusting for timezone.
 * @param {string} date - The date string to format.
 * @param {object} options - Formatting options including locales and timezone handling.
 * @param {string[]} [options.locales=[]] - BCP 47 language tags.
 * @return {string} The formatted date-time string.
 * @example
 * formatIntlDateTimeMedium("2023-04-05T12:00", { locales: ["en-US"] }); // Returns "Apr 5, 2023, 12:00:00 PM"
 */
export const formatIntlDateTimeMedium = (
  date: string,
  { locales = [] }: { locales?: string[] } = {},
): string => {
  const dateObj = new Date(ensureUTC(date));
  return new Intl.DateTimeFormat(locales, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  })
    .format(dateObj)
    .replace(',', '');
};

/**
 * Simplifies a date string from "Month YYYY" to "Mon YYYY".
 * @param {string} date - The date string to simplify.
 * @return {string} The simplified date string.
 * @example
 * shortenMonthAndYear("April 2023"); // Returns "Apr 2023"
 */
export const shortenMonthAndYear = (date: string): string => {
  const [month, year] = date.split(' ');
  return `${month.slice(0, 3)} ${year}`;
};

/**
 * Gets the month and year, subtracting a given number of months.
 * @param {Date} date - The reference date.
 * @param {number} subtractMonth - The number of months to subtract.
 * @return {string} The resulting month and year.
 * @example
 * getCurrentMonthAndYearSubtractMonth(new Date("2023-04-05"), 1); // Returns "March 2023"
 */
export const getCurrentMonthAndYearSubtractMonth = (
  date: Date,
  subtractMonth: number,
): string => {
  const prvDateMonth = new Date(
    date.getFullYear(),
    date.getMonth() - subtractMonth,
    1,
  );
  return `${prvDateMonth.toLocaleString('en-US', {
    month: 'long',
  })} ${prvDateMonth.getFullYear()}`;
};

/**
 * Gets an internationalized string representing the relative time between two date strings.
 * @param {string} dateStr1 - The first date string.
 * @param {string} [dateStr2=new Date().toISOString()] - The second date string.
 * @param {string | string[]} locales - BCP 47 language tag(s).
 * @return {string} The relative time between the two dates.
 * @example
 * relativeTimeBetweenDates("2023-04-04T12:00Z", "2023-04-05T12:00Z", "en-US"); // Returns "1 day ago"
 */
export const relativeTimeBetweenDates = (
  dateStr1: string,
  dateStr2: string = new Date().toISOString(),
  locales: string | string[] = [],
): string => {
  const d1: Date = new Date(ensureUTC(dateStr1));
  const d2: Date = new Date(ensureUTC(dateStr2));
  const rtf: Intl.RelativeTimeFormat = new Intl.RelativeTimeFormat(locales, {
    numeric: 'auto',
  });
  const elapsed: number = d1.getTime() - d2.getTime();

  for (const unit in MILISEC_INTERVALS) {
    if (Math.abs(elapsed) > MILISEC_INTERVALS[unit] || unit === 'second') {
      return rtf.format(
        Math.round(elapsed / MILISEC_INTERVALS[unit]),
        unit as Intl.RelativeTimeFormatUnit,
      );
    }
  }
  return '';
};

/**
 * Calculates the number of days between two date strings.
 * @param {string} dateStr1 - The first date string.
 * @param {string} [dateStr2=new Date().toISOString()] - The second date string.
 * @return {number} The number of days between the two dates.
 * @example
 * daysBetweenDates("2023-04-05", "2023-04-01"); // Returns 4
 */
export const daysBetweenDates = (
  dateStr1: string,
  dateStr2: string = new Date().toISOString(),
): number => {
  const d1: Date = new Date(ensureUTC(dateStr1));
  const d2: Date = new Date(ensureUTC(dateStr2));
  return Math.round((d1.getTime() - d2.getTime()) / MILISEC_INTERVALS.day);
};

interface DateFormatConfig {
  monthFormat?: string;
  dayFormat?: string;
  showDay?: boolean;
  showTime?: boolean;
}

export const formatReadableDate = (
  date: string | Date,
  config: DateFormatConfig = {},
) => {
  const {
    monthFormat = 'MMMM',
    dayFormat = 'Do',
    showDay = false,
    showTime = false,
  } = config;

  const formattedDate = dayjs(date).format(
    showDay ? `${monthFormat} ${dayFormat}, YYYY` : `${monthFormat} YYYY`,
  );
  return showTime
    ? `${formattedDate} ${dayjs(date).format('hh:mm A')}`
    : formattedDate;
};

/**
 * Calculates the number of months remaining until a given date.
 * @param targetDate - The target date in YYYY-MM-DD format.
 * @returns The number of months remaining until the target date.
 */
export const monthsUntil = (targetDate: string): number => {
  const now = dayjs();
  const futureDate = dayjs(targetDate);
  return futureDate.diff(now, 'month');
};

/**
 * Calculates the number of days remaining until a given date.
 * @param targetDate - The target date in YYYY-MM-DD format.
 * @returns The number of days remaining until the target date.
 */
export const daysUntil = (targetDate: string): number => {
  const now = dayjs().startOf('day'); // Avoid differences caused by time-of-day
  const futureDate = dayjs(targetDate).startOf('day');
  return futureDate.diff(now, 'day');
};

/**
 * Checks whether a date string represents a date in the past.
 *
 * @param dateStr - The date string to check.
 * @returns `true` if the date is in the past, otherwise `false`.
 */
export const isDateInPast = (dateStr: string): boolean => {
  return dayjs(dateStr).isBefore(dayjs(), 'day');
};

/**
 * Returns a Date object representing the date `n` days before the current date,
 * with the time set to 00:00:00 in the local timezone.
 * @example
 * ```typescript
 * const result = dateMinusNDays(7);
 * console.log(result); // Outputs: Wed May 07 2025 00:00:00 GMT-0300 (Argentina Standard Time)
 * ```
 */
export const dateMinusNDays = (n: number) => {
  // Validate that n is a non-negative integer
  if (!Number.isInteger(n) || n < 0) {
    throw new Error('Parameter n must be a non-negative integer');
  }
  // Subtract n days, set to start of day, and convert to Date
  return dayjs().subtract(n, 'day').startOf('day').toDate();
};

export function applyTimeToDate(
  timeStr: string,
  baseDate: Date = new Date(),
): Date {
  const [time, period] = timeStr.trim().split(' ');
  const [hourStr, minuteStr] = time.split(':');

  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  // Convert to 24-hour format
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;

  // Clone date and set time
  const dateWithTime = new Date(baseDate);
  dateWithTime.setHours(hour, minute, 0, 0);

  return dateWithTime;
}

/**
 * Get the number of days between 2 dates
 * @param {Object} date1 Date object or valid date string
 * @param {Object} date2 Date object or valid date string
 * @returns {Number} the difference in days
 */
export const getDayDifference = (
  date1: Date | string,
  date2: Date | string,
) => {
  const oneDay = 24 * 60 * 60 * 1000;
  const firstDate = new Date(date1);
  const secondDate = new Date(date2);
  return Math.round(
    Math.abs((firstDate.getTime() - secondDate.getTime()) / oneDay),
  );
};

/**
 * Formats a given date value into "MMM D" format (e.g., "Jul 14").
 * Accepts string (ISO, RFC, etc.), number (timestamp), or Date objects.
 * Returns "Invalid Date" if the input is not a valid date.
 *
 * @param date - The date value to format.
 * @returns {string} The formatted date as a string in "MMM D" format, or "Invalid Date" if input is invalid.
 *
 * @example
 * formatToMonthDay("2025-07-14"); // "Jul 14"
 * formatToMonthDay(new Date("2025-12-01")); // "Dec 1"
 * formatToMonthDay(1754265600000); // "Jul 14"
 * formatToMonthDay(""); // "Invalid Date"
 * formatToMonthDay(null); // "Invalid Date"
 * formatToMonthDay(undefined); // "Invalid Date"
 * formatToMonthDay({}); // "Invalid Date"
 * formatToMonthDay([]); // "Invalid Date"
 */
export function formatToMonthDay(date: unknown): string {
  if (
    date === null ||
    date === undefined ||
    (typeof date === 'string' && date.trim() === '')
  ) {
    return 'Invalid Date';
  }

  // Accept string, number, or Date
  if (
    typeof date === 'string' ||
    typeof date === 'number' ||
    date instanceof Date
  ) {
    const d = dayjs(date);
    if (!d.isValid()) {
      return 'Invalid Date';
    }
    return d.format('MMM D');
  }

  // Otherwise, invalid type
  return 'Invalid Date';
}

/**
 * Formats a given date value into numeric "MM/DD" format (e.g., "10/05").
 * Accepts string (ISO, RFC, etc.), number (timestamp), or Date objects.
 * Returns "Invalid Date" if the input is not a valid date.
 *
 * Month-first (MM/DD).
 *
 * @param date - The date value to format.
 * @returns {string} The formatted date as "MM/DD", or "Invalid Date" if input is invalid.
 *
 * @example
 * formatToMonthDayNumeric("2025-10-05"); // "10/05"
 * formatToMonthDayNumeric(new Date("2025-12-01")); // "12/01"
 * formatToMonthDayNumeric(""); // "Invalid Date"
 * formatToMonthDayNumeric(null); // "Invalid Date"
 * formatToMonthDayNumeric(undefined); // "Invalid Date"
 * formatToMonthDayNumeric({}); // "Invalid Date"
 * formatToMonthDayNumeric([]); // "Invalid Date"
 */
export function formatToMonthDayNumeric(date: unknown): string {
  if (
    date === null ||
    date === undefined ||
    (typeof date === 'string' && date.trim() === '')
  ) {
    return 'Invalid Date';
  }

  // Accept string, number, or Date
  if (
    typeof date === 'string' ||
    typeof date === 'number' ||
    date instanceof Date
  ) {
    const d = dayjs(date);
    if (!d.isValid()) {
      return 'Invalid Date';
    }
    return d.format('MM/DD');
  }

  // Otherwise, invalid type
  return 'Invalid Date';
}
